/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened, and
 * steps between zoom dialogs like a carousel via [data-zoom-nav] buttons, arrow keys, or horizontal swipes.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleNavClick, opts);
    this.addEventListener('keydown', this.#handleNavKey, opts);
    this.addEventListener('touchstart', this.#handleTouchStart, { ...opts, passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };


  /** Zoom dialogs in gallery order. */
  #zoomDialogs() {
    return [...this.querySelectorAll('dialog')].filter((d) => d.querySelector('img[data-zoom-image]'));
  }

  #handleNavClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-zoom-nav]');
    if (!control || !this.contains(control)) return;
    const dialog = control.closest('dialog');
    if (!dialog) return;
    this.#step(dialog, control.getAttribute('data-zoom-nav') === 'prev' ? -1 : 1, control.getAttribute('data-zoom-nav'));
  };

  #handleNavKey = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!(event.target instanceof Element)) return;
    const dialog = event.target.closest('dialog');
    if (!dialog?.open || !this.contains(dialog) || !dialog.querySelector('[data-zoom-nav]')) return;
    event.preventDefault();
    const rtl = getComputedStyle(dialog).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== rtl;
    this.#step(dialog, forward ? 1 : -1, null);
  };


  #touchStart = null;

  #handleTouchStart = (event) => {
    if (event.touches.length !== 1 || !(event.target instanceof Element)) {
      this.#touchStart = null;
      return;
    }
    const dialog = event.target.closest('dialog');
    if (!dialog?.open || !dialog.querySelector('[data-zoom-nav]')) return;
    if (event.target.closest('button')) return;
    const touch = event.touches[0];
    this.#touchStart = { x: touch.clientX, y: touch.clientY, dialog };
  };

  /** A clear horizontal swipe (≥50px, more sideways than vertical) steps to the neighbouring photo. */
  #handleTouchEnd = (event) => {
    const start = this.#touchStart;
    this.#touchStart = null;
    if (!start || event.changedTouches.length !== 1) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (window.visualViewport && window.visualViewport.scale > 1.05) return;
    const rtl = getComputedStyle(start.dialog).direction === 'rtl';
    const forward = (dx < 0) !== rtl;
    this.#step(start.dialog, forward ? 1 : -1, null);
  };

  /** Close the current zoom dialog and open its neighbour (wrapping), keeping focus on the same control. */
  #step(current, delta, focusNav) {
    const dialogs = this.#zoomDialogs();
    const index = dialogs.indexOf(current);
    if (index === -1 || dialogs.length < 2) return;
    const next = dialogs[(index + delta + dialogs.length) % dialogs.length];

    const image = next.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';

    current.close();
    next.showModal();

    const target = focusNav ? next.querySelector(`[data-zoom-nav="${focusNav}"]`) : null;
    if (target instanceof HTMLElement) target.focus();
  }

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
